/**
 * Configs del detalle de review por sección. YA NO se escriben a mano: se PROYECTAN del
 * diccionario único `@idc-forms/schemas` (`fields.ts`) vía `toSectionDetailConfig`. Así
 * el label/formato de un campo se define UNA sola vez (en el diccionario) y el review lo
 * lee — sin duplicación. Antes este archivo repetía a mano lo que vivía en `fields.ts`.
 */
import { getSectionFields, type PortalClientType, type PortalSectionId } from '@idc-forms/schemas'
import { CATALOG_TYPE_ENTITY } from '@/shared/catalogs/entities'
import { CATALOG_NON_PROFIT_ENTITY } from '@/shared/catalogs/non-profit-entities'
import { CATALOG_PROFIT_ENTITY } from '@/shared/catalogs/profit-entities'
import { CATALOG_SOURCE_INCOME } from '@/shared/catalogs/source-income'
import { entityUsesOtherSociety, isProfitEntity, showsShareholders } from '../form/company-defaults'
import { toSectionDetailConfig } from './field-kind-format'
import type {
  DetailFieldDef,
  SectionDetailConfig,
  SectionVisiblePredicate
} from './section-detail-review'

/**
 * Visibilidad condicional del review de la EMPRESA por tipo de entidad — espeja los pasos
 * condicionales del formulario (una sola fuente: los predicados viven en company-defaults).
 * `fields.ts` es metadata pura (sin lógica de tipo); acá se le adjunta el `visibleWhen` a
 * los grupos/arrays que sólo aplican a ciertos tipos, para que el asesor NO vea a la vez
 * "Sociedad mercantil" y "Otro tipo de sociedad", ni accionistas/órgano en una no lucrativa.
 */
const COMPANY_GROUP_VISIBILITY: Record<string, SectionVisiblePredicate> = {
  'Sociedad mercantil': isProfitEntity,
  'Otro tipo de sociedad': (d) => entityUsesOtherSociety(d?.entityType as string | undefined),
  'Estructura accionaria': isProfitEntity
}
const COMPANY_ARRAY_VISIBILITY: Record<string, SectionVisiblePredicate> = {
  'shareholdingStructure.individual': showsShareholders,
  'shareholdingStructure.juridical': showsShareholders,
  membersOfBoard: showsShareholders,
  'commercialSociety.modificationPublicDeed': isProfitEntity,
  'otherSociety.modificationCreationDocument': (d) =>
    entityUsesOtherSociety(d?.entityType as string | undefined)
}

/** value→label desde un catálogo [{value,label}] (muestra el label amigable en review). */
function catalogFormatter(
  catalog: ReadonlyArray<{ value: string; label: string }>
): DetailFieldDef['format'] {
  const byValue = new Map(catalog.map((o) => [o.value, o.label]))
  return (value) => {
    if (value == null || value === '') return '—'
    return byValue.get(String(value)) ?? String(value)
  }
}

/**
 * Adjunta un `format` a los campos cuyo path esté en `byPath`. Los escalares se indexan por
 * su path; los de array por `<arrayPath>.<fieldPath>` (evita colisiones entre, p.ej., el
 * `type` de una cuenta bancaria y el `type` de una fuente de ingreso). Es un override
 * puntual y aditivo: sólo pisa el formato proyectado del diccionario donde haya una entrada.
 */
export function attachFormattersByPath(
  config: SectionDetailConfig,
  byPath: Record<string, DetailFieldDef['format']>
): SectionDetailConfig {
  return {
    scalarGroups: config.scalarGroups?.map((g) => ({
      ...g,
      fields: g.fields.map((f) => (byPath[f.path] ? { ...f, format: byPath[f.path] } : f))
    })),
    arrays: config.arrays?.map((a) => ({
      ...a,
      fields: a.fields.map((f) => {
        const fmt = byPath[`${a.path}.${f.path}`]
        return fmt ? { ...f, format: fmt } : f
      }),
      fieldGroups: a.fieldGroups?.map((group) => ({
        ...group,
        fields: group.fields.map((field) => {
          const fmt = byPath[`${a.path}.${field.path}`]
          return fmt ? { ...field, format: fmt } : field
        })
      })),
      nestedArrays: a.nestedArrays?.map((nested) => ({
        ...nested,
        fields: nested.fields.map((field) => {
          const fmt = byPath[`${a.path}.${nested.path}.${field.path}`]
          return fmt ? { ...field, format: fmt } : field
        })
      }))
    }))
  }
}

function withLegalRepresentativeStructure(
  config: SectionDetailConfig,
  pepConfig: SectionDetailConfig
): SectionDetailConfig {
  return {
    ...config,
    arrays: config.arrays?.map((array) => {
      if (array.path !== 'representatives') return array
      const byPath = new Map(array.fields.map((field) => [field.path, field]))
      const fields = (paths: string[]) =>
        paths.flatMap((path) => {
          const field = byPath.get(path)
          return field ? [field] : []
        })
      return {
        ...array,
        fieldGroups: [
          {
            title: 'Poder o nombramiento',
            fields: fields([
              'qualityRepresentative',
              'powerRegister.name',
              'powerRegister.number',
              'powerRegister.sheet',
              'powerRegister.book'
            ])
          },
          {
            title: 'Identificación',
            fields: fields([
              'name.first',
              'identificationCard.type',
              'identificationCard.number',
              'identificationCard.dateDue',
              'identificationCard.emission.country'
            ])
          },
          {
            title: 'Información personal',
            fields: fields([
              'birthdate',
              'birthplace.country',
              'birthplace.department',
              'birthplace.municipality',
              'gender',
              'civilStatus',
              'profession',
              'otherProfession',
              'nationality',
              'migrationStatus',
              'otherMigrationStatus',
              'nit',
              'economicActivityRTU'
            ])
          },
          {
            title: 'Residencia y contacto',
            fields: fields([
              'residence.country',
              'residence.department',
              'residence.municipality',
              'residence.address',
              'residence.zone',
              'email',
              'phone'
            ])
          },
          {
            title: 'Declaración PEP',
            fields: fields(['isPep', 'hasRelativesPep', 'hasPartnersPep', 'isCPE'])
          }
        ],
        nestedArrays: pepConfig.arrays
      }
    })
  }
}

/**
 * Overrides de formato por sección para campos que el diccionario marca como `text`/`number`
 * pero cuyo valor persistido es un CÓDIGO de catálogo (letra/clave). El diccionario es
 * framework-free (no conoce los catálogos de la web); acá se le adjunta el lookup para que
 * el asesor vea el texto amigable ("ENTIDAD LUCRATIVA", "Rentas") en vez del código crudo.
 *   - company: tipo de entidad y su detalle (A/B… y A1–A11 / B1–B12).
 *   - economic: la fuente de "otra fuente de ingreso" (P/M/R/J/O) del flujo individual.
 * (`jurisdiction` NO va acá: es un país — se corrige en el diccionario con `kind: 'country'`;
 *  la `profession` del representante legal SÍ necesita runtime → la inyecta su página.)
 */
const FORMATTERS_BY_SECTION: Partial<
  Record<PortalSectionId, Record<string, DetailFieldDef['format']>>
> = {
  company: {
    entityType: catalogFormatter(CATALOG_TYPE_ENTITY),
    entityDetail: catalogFormatter([...CATALOG_PROFIT_ENTITY, ...CATALOG_NON_PROFIT_ENTITY])
  },
  economic: {
    'otherIncomes.type': catalogFormatter(CATALOG_SOURCE_INCOME)
  }
}

/** Adjunta los `visibleWhen` de la empresa al config proyectado (por título/path). */
function withCompanyVisibility(config: SectionDetailConfig): SectionDetailConfig {
  return {
    scalarGroups: config.scalarGroups?.map((g) => ({
      ...g,
      visibleWhen: g.title ? COMPANY_GROUP_VISIBILITY[g.title] : undefined
    })),
    arrays: config.arrays?.map((a) => ({
      ...a,
      visibleWhen: COMPANY_ARRAY_VISIBILITY[a.path]
    }))
  }
}

/**
 * Config de detalle PODADA por tipo de persona: descarta campos que no aplican (p.ej. el
 * cónyuge del patrimonial en un flujo jurídico), con los formatters de catálogo de la
 * sección adjuntos y — para la empresa — la visibilidad condicional por tipo de entidad.
 */
export function sectionDetailConfig(
  id: PortalSectionId,
  clientType?: PortalClientType
): SectionDetailConfig {
  const fields = getSectionFields(id, clientType)
  if (!fields) return {}
  const overrides = FORMATTERS_BY_SECTION[id]
  const base = overrides
    ? attachFormattersByPath(toSectionDetailConfig(fields), overrides)
    : toSectionDetailConfig(fields)
  return id === 'company' ? withCompanyVisibility(base) : base
}

export const APPLICANT_DETAIL_CONFIG = sectionDetailConfig('applicant')
export const COMPANY_DETAIL_CONFIG = sectionDetailConfig('company')
export const PEP_DETAIL_CONFIG = sectionDetailConfig('pep')
export const ECONOMIC_DETAIL_CONFIG = sectionDetailConfig('economic')
export const EQUITY_DETAIL_CONFIG = sectionDetailConfig('equity')
export const LEGAL_REP_DETAIL_CONFIG = withLegalRepresentativeStructure(
  sectionDetailConfig('legal_representatives'),
  PEP_DETAIL_CONFIG
)

/**
 * Config por sección (para resolver etiquetas de campo legibles en el portal). `applicant`
 * va PRIMERO: cuando hay que resolver un campo sin conocer su sección (ver `resolveFieldLabel`),
 * se itera este mapa en orden y gana el primer match — los campos más comunes son de applicant.
 */
const CONFIG_BY_SECTION: Record<string, SectionDetailConfig> = {
  applicant: APPLICANT_DETAIL_CONFIG,
  company: COMPANY_DETAIL_CONFIG,
  pep: PEP_DETAIL_CONFIG,
  economic: ECONOMIC_DETAIL_CONFIG,
  equity: EQUITY_DETAIL_CONFIG,
  legal_representatives: LEGAL_REP_DETAIL_CONFIG
}

/**
 * Etiqueta legible de un campo a partir de (sección, dot-path). Cubre campos escalares
 * (`monthlyIncome` → "Ingresos mensuales") y campos de registros
 * (`pep.0.position` → "Persona PEP 1 · Puesto que desempeña"). Humaniza las correcciones
 * por campo en el portal del cliente.
 */
/**
 * Intento de resolución: `undefined` si la config NO describe el campo. Distinguir "no
 * resolví" de "resolví" es lo que permite a `resolveFieldLabel` seguir probando otras
 * secciones/tipos en vez de quedarse con una etiqueta a medias. Por eso un subcampo
 * desconocido dentro de un array conocido cuenta como NO resuelto: `monthlyIncomes.0.currency`
 * existe como array en el modelo individual pero `currency` sólo existe en el jurídico, y
 * devolver "Ingreso mensual 1 · currency" (mitad español, mitad path) es peor que seguir buscando.
 */
function trySectionFieldLabel(
  section: string,
  fieldPath: string,
  clientType?: PortalClientType
): string | undefined {
  // Con clientType, la config se resuelve por tipo de persona (la económica jurídica es
  // un modelo distinto; el cónyuge del patrimonial no aplica). Sin él, cae al estático.
  const config = clientType
    ? sectionDetailConfig(section as PortalSectionId, clientType)
    : CONFIG_BY_SECTION[section]
  if (!config) return undefined

  for (const group of config.scalarGroups ?? []) {
    const field = group.fields.find((f) => f.path === fieldPath)
    if (field) return field.label
  }
  // Array completo sin índice (solicitud de registro a un array vacío): su nombre.
  const exactArray = (config.arrays ?? []).find((a) => a.path === fieldPath)
  if (exactArray) return exactArray.itemTitle
  for (const array of config.arrays ?? []) {
    const prefix = `${array.path}.`
    if (!fieldPath.startsWith(prefix)) continue
    const rest = fieldPath.slice(prefix.length) // `${index}` o `${index}.${subpath}`
    const dot = rest.indexOf('.')
    // Item de array completo, sin subcampo (p.ej. `ownBusinesses.0`): se marcó/pidió corregir
    // el registro entero, no un campo puntual → sólo el título del ítem numerado.
    if (dot === -1) {
      const itemIndex = Number(rest)
      if (Number.isNaN(itemIndex)) continue
      return `${array.itemTitle} ${itemIndex + 1}`
    }
    const index = Number(rest.slice(0, dot))
    const sub = rest.slice(dot + 1)
    for (const nested of array.nestedArrays ?? []) {
      const nestedPrefix = `${nested.path}.`
      if (!sub.startsWith(nestedPrefix)) continue
      const nestedRest = sub.slice(nestedPrefix.length)
      const nestedDot = nestedRest.indexOf('.')
      if (nestedDot === -1) continue
      const nestedIndex = Number(nestedRest.slice(0, nestedDot))
      const nestedSub = nestedRest.slice(nestedDot + 1)
      const nestedField = nested.fields.find((candidate) => candidate.path === nestedSub)
      if (!nestedField) return undefined
      return `${array.itemTitle} ${index + 1} · ${nested.itemTitle} ${nestedIndex + 1} · ${nestedField.label}`
    }
    const field = [
      ...array.fields,
      ...(array.fieldGroups?.flatMap((group) => group.fields) ?? [])
    ].find((f) => f.path === sub)
    if (!field) return undefined
    return `${array.itemTitle} ${Number.isNaN(index) ? '' : index + 1} · ${field.label}`.trim()
  }
  return undefined
}

/**
 * Etiqueta legible de un campo a partir de (sección, dot-path). Cubre campos escalares
 * (`monthlyIncome` → "Ingresos mensuales") y campos de registros
 * (`pep.0.position` → "Persona PEP 1 · Puesto que desempeña"). Humaniza las correcciones
 * por campo en el portal del cliente.
 */
export function sectionFieldLabel(
  section: string,
  fieldPath: string,
  clientType?: PortalClientType
): string {
  return trySectionFieldLabel(section, fieldPath, clientType) ?? fieldPath
}

/** Tipos de persona a probar cuando la bitácora no registró cuál era. */
const CLIENT_TYPES: PortalClientType[] = ['individual', 'juridical']

/**
 * Etiqueta legible de un campo cuando puede que NO conozcamos su sección ni el tipo de
 * persona. La bitácora (`fields_reviewed`, `field_edited`) persiste el path del campo pero
 * no siempre su sección, y el timeline se renderiza desde vistas que no todas conocen al
 * cliente — así que el Historial no puede llamar a `sectionFieldLabel` directo.
 *
 * Prueba cada (sección × tipo de persona) conocido y devuelve la primera etiqueta que
 * resuelva COMPLETA. Probar ambos tipos es lo que rescata los campos exclusivos del modelo
 * jurídico (la económica jurídica es un catálogo aparte: `sourceFunds`, `suppliers`,
 * `numberEmployees`…), que si no salían como path crudo. Fallback: el path tal cual.
 */
export function resolveFieldLabel(
  field: string,
  section?: string,
  clientType?: PortalClientType
): string {
  const sections = section ? [section] : Object.keys(CONFIG_BY_SECTION)
  const types = clientType ? [clientType] : CLIENT_TYPES
  for (const candidateSection of sections) {
    for (const candidateType of types) {
      const label = trySectionFieldLabel(candidateSection, field, candidateType)
      if (label) return label
    }
  }
  return field
}
